import React, { useEffect, useState } from "react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { Heart, Plus, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api";

const get = (url) => api.get(url).then((r) => r.data);
const post = (url, body) => api.post(url, body).then((r) => r.data);

function money(n) { return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function Donations() {
  const [rows, setRows] = useState([]);
  const [donors, setDonors] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", donor_id: "", campaign_id: "", method: "card", note: "" });
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [d, do2, c] = await Promise.all([get("/api/donations"), get("/api/donors"), get("/api/campaigns")]);
      if (cancelled) return;
      setRows(d); setDonors(do2); setCampaigns(c);
    })();
    return () => { cancelled = true; };
  }, []);

  const reload = async () => {
    const [d, do2, c] = await Promise.all([get("/api/donations"), get("/api/donors"), get("/api/campaigns")]);
    setRows(d); setDonors(do2); setCampaigns(c);
  };

  const submit = async () => {
    try {
      setBusy(true);
      await post("/api/donations", {
        ...form,
        amount: Number(form.amount),
        donor_id: form.donor_id || null,
        campaign_id: form.campaign_id || null,
      });
      toast({ title: "Donation recorded" });
      setOpen(false); setForm({ amount: "", donor_id: "", campaign_id: "", method: "card", note: "" });
      reload();
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="Donations" subtitle="Every gift received, tied to the campaign and program it served.">
        <button onClick={() => setOpen(true)} data-testid="donations-new"
          className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm font-medium">
          <Plus size={14} /> Record donation
        </button>
      </PageHeader>
      <PageBody>
        {open && (
          <div className="rounded-xl border border-border bg-card p-5 mb-5 space-y-3">
            <h3 className="font-medium text-sm">New donation</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <input data-testid="donations-amount" placeholder="Amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="px-3 py-2 rounded border border-border bg-background" />
              <select value={form.donor_id} onChange={(e) => setForm({ ...form, donor_id: e.target.value })} className="px-3 py-2 rounded border border-border bg-background">
                <option value="">-- Donor (optional) --</option>
                {donors.map((d) => <option key={d.id} value={d.id}>{d.full_name}</option>)}
              </select>
              <select value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} className="px-3 py-2 rounded border border-border bg-background">
                <option value="">-- Campaign (optional) --</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="px-3 py-2 rounded border border-border bg-background">
                {["card", "bank", "cash", "check", "in_kind", "other"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <input placeholder="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="px-3 py-2 rounded border border-border bg-background md:col-span-2" />
            </div>
            <div className="flex gap-2">
              <button onClick={submit} disabled={busy || !form.amount} data-testid="donations-submit" className="inline-flex items-center gap-1 px-3 py-2 rounded bg-primary text-primary-foreground text-sm">
                {busy && <Loader2 size={14} className="animate-spin" />} Record
              </button>
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded border border-border text-sm">Cancel</button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground bg-accent/30">
              <tr><th className="text-left px-4 py-3">Donor</th><th className="text-left px-4 py-3">Campaign</th><th className="text-left px-4 py-3">Method</th><th className="text-right px-4 py-3">Amount</th><th className="text-left px-4 py-3">Received</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">
                <Heart size={28} className="mx-auto mb-2 opacity-30" /> No donations recorded yet.
              </td></tr>}
              {rows.map((d) => (
                <tr key={d.id} className="border-t border-border/50" data-testid={`donation-${d.id}`}>
                  <td className="px-4 py-2.5"><div className="font-medium">{d.donor_name || "Anonymous"}</div><div className="text-xs text-muted-foreground">{d.donor_email || "—"}</div></td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{campaigns.find((c) => c.id === d.campaign_id)?.name || "—"}</td>
                  <td className="px-4 py-2.5"><span className="text-xs px-2 py-0.5 rounded bg-accent">{d.method}</span></td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium">{money(d.amount)}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{d.received_on}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageBody>
    </>
  );
}
